import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { trainings } from "../data/trainings";
import Header from "../components/Header";
import Footer from "../components/Footer";

const TrainingDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [training, setTraining] = useState(null);

  useEffect(() => {
    const foundTraining = trainings.find((t) => t.slug === slug);
    if (foundTraining) {
      setTraining(foundTraining);
    }
  }, [slug]);

  if (!training) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">
            Training not found
          </h1>
          <button
            onClick={() => navigate("/trainings")}
            className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            Back to Trainings
          </button>
        </div>
      </div>
    );
  }

  const imgClass = training?.imageContain
    ? "w-full object-contain rounded-2xl shadow-lg bg-gray-50"
    : "w-full h-80 object-cover rounded-2xl shadow-lg";

  const renderSection = (section, index) => {
    if (section.imagePosition === "center") {
      return (
        <p key={index} className="text-gray-700 leading-relaxed">
          {section.text}
        </p>
      );
    }

    if (section.imagePosition === "right") {
      return (
        <div
          key={index}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-16"
        >
          <div className="text-gray-700 leading-relaxed">
            <p className={index === 0 ? "text-lg" : ""}>{section.text}</p>
          </div>
          <div className="order-first lg:order-last">
            <img
              src={section.image}
              alt={section.imageAlt}
              className={imgClass}
            />
          </div>
        </div>
      );
    }

    if (section.imagePosition === "left") {
      return (
        <div
          key={index}
          className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-16"
        >
          <div>
            <img
              src={section.image}
              alt={section.imageAlt}
              className={imgClass}
            />
          </div>
          <div className="text-gray-700 leading-relaxed">
            <p>{section.text}</p>
          </div>
        </div>
      );
    }

    return null;
  };

  const centerSections = training.sections
    ? training.sections.filter((s) => s.imagePosition === "center")
    : [];
  const layoutSections = training.sections
    ? training.sections.filter((s) => s.imagePosition !== "center")
    : [];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header activeSection="trainings" setActiveSection={() => {}} />

      {/* Main Content */}
      <main className="pt-16">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden bg-gradient-to-br from-slate-50 via-white to-blue-50">
          <div className="absolute inset-0">
            <div
              className="absolute inset-0 opacity-40"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23f1f5f9' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
              }}
            ></div>
          </div>

          <div className="relative max-w-4xl mx-auto px-4 md:px-8">
            {/* Back button */}
            <button
              onClick={() => navigate("/trainings")}
              className="inline-flex items-center text-teal-600 hover:text-teal-700 font-semibold mb-8 transition-colors"
            >
              <svg
                className="w-5 h-5 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Back to Trainings
            </button>

            {/* Training header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white text-sm font-semibold mb-6">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Training Course
              </div>

              <h1 className="text-4xl md:text-5xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 bg-clip-text text-transparent mb-6">
                {training.title}
              </h1>

              <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-gray-600">
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {training.date}
                </div>
                <div className="flex items-center">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                      clipRule="evenodd"
                    />
                  </svg>
                  {training.location}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Content Section */}
        <section className="py-16 bg-white">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="prose prose-lg max-w-none">
              {/* Layout sections (with images) */}
              {layoutSections.map((section, index) =>
                renderSection(section, index)
              )}

              {/* Centered text-only sections */}
              {centerSections.length > 0 && (
                <div className="text-gray-700 leading-relaxed space-y-6 max-w-4xl mx-auto">
                  {centerSections.map((section, index) =>
                    renderSection(section, `center-${index}`)
                  )}
                </div>
              )}
            </div>

            {/* Facilitators */}
            {training.facilitators && training.facilitators.length > 0 && (
              <div className="mt-12 p-8 bg-gradient-to-br from-teal-50 to-emerald-50 rounded-2xl">
                <h3 className="text-2xl font-bold text-gray-900 mb-6">
                  Facilitators
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {training.facilitators.map((facilitator, index) => (
                    <div key={index} className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center text-white font-bold text-lg mr-4">
                        {facilitator.name
                          .split(" ")
                          .map((n) => n[0])
                          .join("")}
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          {facilitator.name}
                        </h4>
                        <p className="text-gray-600">{facilitator.role}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TrainingDetailPage;
